import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Agent, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { take, tap } from 'rxjs/operators';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class AgentService extends HateoasResourceOperation<Agent> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    model: Agent | null = null;
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(Agent);
        this.apiUrl = this.environment.frontdoorURL;

    }


    public findAgents(options?: PagedGetOption): Promise<Agent[]> {
        return super
            .getPage({ ...AgentService.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((agents) => (agents ? agents.resources : []));
    }


    public getEnrollmentToken(id: string, orgId: string) {
        const fullpath = `${this.apiUrl}/${id}/agents/enrollment`;
        return this.http
            .post(fullpath, {organizationId: orgId})
    }

    public pingAgent(frontdoorId: string, id:string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/${frontdoorId}/agents/${id}/version`;
            return this.http
                .get(fullpath, {headers: headers})
    }



    public delete(frontdoorId: string, id: string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/${frontdoorId}/environments/${id}`;
            return this.http
                .delete(fullpath, {headers: headers})
    }

    public getAccountById(orgId: string) {
        const fullpath = `${this.apiUrl}?organizationId=${orgId}`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public createAccount(orgId: string) {
        const fullpath = `${this.apiUrl};`
        return this.http
            .post(fullpath, {organizationId: orgId}) 
    }

    public getAgents() {
        const fullpath = `${this.apiUrl}/agents`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getAgentsByEnv(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/environments`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerFrontdoor(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/metrics`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerShare(frontdoorId: string, id: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/shares/${id}/metrics`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getMetricsPerEnv(frontdoorId: string, envId: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/environments/${envId}/metrics`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getHealthChecks(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/health-checks`
        return this.http
        .get(fullpath, {
    headers: { Accept: 'application/hal+json' }}) 
    }

    public getSparklines(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/${frontdoorId}/sparklines`
        return this.http
        .get(fullpath,{
    headers: { Accept: 'application/hal+json' }})
    }


    public getModel(): Agent | null {
            return this.model;
        }
        
    public setModel(data: Agent) {
            this.model = data;
        }


    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

                  return headers.append('Content-Type', 'application/json');
    }

        /**
     * Generic error handler for bad requests
     */
        protected handleError(scope: any, error: HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred. Handle it accordingly.
                scope.logger.error('Feature Service error occurred:', error.error);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
            }
            scope.lastErrorSource.next(error);
            return observableThrowError(error);
        }
}
